import { Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { TreeViewAllModule } from '@syncfusion/ej2-angular-navigations';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Constants, MenuCategoryId, UserRole } from '../../core/const/constants';
import { HelperFunctionService } from '../../core/services/helper-function.service';
import { ScreenHelper, StorageConstants, TranslatePipe } from '@techextensor/tab-core-utility';
import { PermissionService } from '../../core/services/permission.service';
import { NgClass, NgIf } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TabSdk } from '@techextensor/tab-sdk';

@Component({
    selector: 'app-menu-bar',
    imports: [TreeViewAllModule, NgIf, NgClass, TranslatePipe],
    templateUrl: './menu-bar.component.html',
    styleUrl: './menu-bar.component.scss'
})
export class MenuBarComponent {
  public activeScreen: any;
  public fields: any;
  @ViewChild('menuTree') menuTree: any;
  private readonly router: Router = inject(Router);
  public readonly route: ActivatedRoute = inject(ActivatedRoute);
  private helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly _tabScreenHelper: ScreenHelper = inject(ScreenHelper);
  public collapsed = false;
  private destroy$ = new Subject<void>();
  @Input() isAdminRoute: boolean = false;
  @Output() panelToggle = new EventEmitter<boolean>();

  constructor() { }

  ngOnInit(): void {
    // Update the active screen based on the current URL
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.updateActiveScreen();
        }
      });

    //Load menu data
    this.loadMenuData();
  }

  /**
   * The ngOnDestroy() method is called when the component is about to be destroyed.
   * In this method, we complete the destroy$ observable to indicate that the component is no longer needed.
   */
  ngOnDestroy(): void {
    // Complete the destroy$ observable to indicate that the component is no longer needed
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  loadMenuData() {
    const appInfo = TabSdk.store.getAppInfo() ?? {};

    const isAppBuilder = this.permissionService.doesUserIs(UserRole.AppBuilderRoleName);
    const isAppAdmin = this.permissionService.doesUserIs(UserRole.AppAdministratorRoleName);
    const isTenantAdmin = this.permissionService.doesUserIsTenantAdministrator();
    const isTabAdmin = this.permissionService.doesUserIsTabAdministrator();

    const CategoryIds: any = [];

    if (this.isAdminRoute) {
      // 1. Tenant Admin
      //    - Tab Admin or (AppCode === TAB && Role === Tenant Admin)
      if (isTabAdmin || isTenantAdmin) {
        CategoryIds.push(MenuCategoryId.TenantAdmin);
      }

      // 2. Tab Administrator
      if (isTabAdmin) {
        CategoryIds.push(MenuCategoryId.TabAdministrator);
      }
    } else {
      // 1. Administration
      //    - Tab Admin or (AppCode && AppCode !== TAB && (Role === App Admin || Role === Tenant Admin))
      if (isTabAdmin || isTenantAdmin || isAppBuilder || isAppAdmin) {
        CategoryIds.push(MenuCategoryId.Administration);
      }

      // 2. App Builder
      //    - Tab Admin or (AppCode.TenantId === user.TenantId && (Role === App Admin))
      if (
        isTabAdmin ||
        (appInfo?.TenantId?.Id === TabSdk.store.tenantId && isAppBuilder)
      ) {
        CategoryIds.push(MenuCategoryId.AppBuilder);
      }

      // 3. Application
      //    - Default
      CategoryIds.push(MenuCategoryId.Application);
    }

    this.helperFunctionService
      .getDSQData(Constants.Header_TABMD_Menu_Items_Id, { CategoryId: CategoryIds.join(',') })
      .subscribe((response: any) => {
        this.fields = this.generateMenuFieldsFromData(response?.Result ?? []);
        // Update the selected menu item based on the current URL
        setTimeout(() => {
          this.updateActiveScreen();
        }, 500);
      });
  }

  /**
   * Updates the active screen based on the current URL.
   * If the URL has a screenId, it will select the corresponding menu item.
   * If the URL does not have a screenId, it will clear the previous selection.
   */
  private updateActiveScreen() {

    if(this.collapsed) return;

    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    // Get the screenId from the URL
    const screenId = currentRoute.snapshot.paramMap.get('screenId');

    // If the URL has a screenId, select the corresponding menu item
    if (screenId && screenId !== '') {
      // Clear the previous selection
      this.clearPreviousSelection(this.activeScreen, screenId);
      // Set the active screen to the new screenId
      // setTimeout(() => {
      this.activeScreen = screenId;
      // Select the active screen
      this.selectActiveScreen();
      // }, 500);
    }
    // If the URL does not have a screenId, clear the previous selection
    else if (!screenId && this.activeScreen) {
      delete this.activeScreen;
      this.clearPreviousSelection(null);
    }
  }

  /**
   * Generates the menu fields from the given data.
   * @param data - The data used to generate the menu fields.
   * @returns The generated menu fields.
   */
  generateMenuFieldsFromData(data: any) {
    let fieldData: any = [];
    // Apply custom sorting logic - same as header component
    data.sort((a: any, b: any) => {
      // Use the MenuCategoryId constant to check for Application category
      const isAApplicationMenu = a?.CategoryId?.toLowerCase() === MenuCategoryId.Application?.toLowerCase();
      const isBApplicationMenu = b?.CategoryId?.toLowerCase() === MenuCategoryId.Application?.toLowerCase();
      
      // If one is application menu and other is not, put application menu last
      if (isAApplicationMenu && !isBApplicationMenu) {
        return 1; // a goes after b
      }
      if (isBApplicationMenu && !isAApplicationMenu) {
        return -1; // b goes after a
      }

      // For all other cases (admin routes, same category, no Application category), sort by sequence as before
      return (a?.Sequence || 0) - (b?.Sequence || 0);
    });

    // Mapping logic simplified
    fieldData = data?.map((item: any) => ({
      id: item['Id'],
      name: item['DisplayName'],
      expanded: false,
      hasChildren: data.some((x: any) => x['ParentID'] === item['Id']),
      parentID: item['ParentID'],
      icon: `e-icons ${item['Icon']}`,
      screenId: item?.ScreenID ?? null,
      selected: false
    }));
    return {
      dataSource: fieldData,
      id: 'id',
      text: 'name',
      child: 'child',
      parentID: 'parentID',
      hasChildren: 'hasChildren',
      isChecked: 'isChecked'
    };
  }

  /**
   * Handles the click event on a menu item.
   * If the menu item has a link, navigates to that link.
   * @param args - The event arguments containing the clicked menu item.
   */
  public onMenuItemClicked(args: any) {
    // Get the data of the clicked menu item
    const nodeData = this.menuTree.getTreeData(args.node)[0];

    // If the menu item has a link, navigate to that link
    if (nodeData?.screenId && nodeData?.screenId != '') {
      TabSdk.store.removeSession(StorageConstants.screenContext);
      if (this.isAdminRoute) {
        this.router.navigate([`/admin/${nodeData?.screenId}`], { queryParamsHandling: 'merge' });
      } else {
        this.router.navigate([`/${TabSdk.store.appName}/screen/${nodeData?.screenId}`], { queryParamsHandling: 'merge' });
      }
    }
  }

  /**
   * Selects the active screen in the menu tree.
   * If the active screen is not set or there are no menu items, does nothing.
   */
  private selectActiveScreen() {
    if (!this.activeScreen || !this.fields?.dataSource?.length) return;

    // Find the menu item that matches the active screen
    let selectedNode = this.fields.dataSource.find(
      (item: { screenId: string }) => item.screenId === this.activeScreen
    );

    if(!selectedNode) {
      const screenDetail = this._tabScreenHelper.getScreen(this.activeScreen);
      if(!screenDetail?.ScreenConfiguration) return;
      
      const screenConfiguration = JSON.parse(screenDetail?.ScreenConfiguration ?? '{}');      
      
      if (screenConfiguration?.menu?.length) {
        selectedNode = this.fields.dataSource.find(
          (item: any) => item.id?.toLowerCase() === screenConfiguration.menu?.toLowerCase()
        );
      }  

      
      if(selectedNode) {
        const parentList = this.getParentListId(selectedNode.id).reverse();
        this.menuTree.expandAll(parentList);      
        
        setTimeout(() => {
          const node = document.querySelector(`li[data-uid="${selectedNode.id}"]`);             
          node?.classList?.add('e-active');
        }, 100);
      }

    } else {
      // Set the selected nodes to the ID of the selected node
      this.menuTree.selectedNodes = [selectedNode.id];

      // Expand all the parent nodes of the selected node
      const parentList = this.getParentListId(selectedNode.id).reverse();
      this.menuTree.expandAll(parentList);
    }
  }

  /**
   * Returns a list of parent node IDs for the given parent node ID.
   * Used to expand all the parent nodes of the selected node.
   * @param parentID - The ID of the parent node.
   * @returns A list of parent node IDs.
   */
  private getParentListId(parentID: any): number[] {
    const parentList: number[] = [];

    let parentNode = this.fields?.dataSource?.find((item: any) => item?.id === parentID);
    while (parentNode) {
      parentList.push(parentNode.id);
      parentNode = this.fields?.dataSource?.find((item: any) => item?.id === parentNode?.parentID);
    }
    return parentList;
  }

  /**
   * Clears the previous selection in the menu tree by collapsing all the parent nodes of the active screen.
   * If the next screen is provided, it will collapse all the parent nodes of the active screen that are not also parents of the next screen.
   * @param activeScreen - The ID of the active screen.
   * @param nextScreen - The ID of the next screen.
   */
  private clearPreviousSelection(activeScreen: any, nextScreen?: any) {
    // Clear the selection
    this.menuTree.selectedNodes = [];

    // if (activeScreen) {
    //   // Find the active screen node
    //   const selectedNode = this.fields?.dataSource?.find((item: any) => item?.screenId === activeScreen);

    //   // Find the next screen node if provided
    //   const nextScreenNode = nextScreen
    //     ? this.fields?.dataSource?.find((item: any) => item?.screenId === nextScreen)
    //     : undefined;

    //   // Get the parent IDs of the active screen node
    //   const selectedParents = this.getParentListId(selectedNode?.id);

    //   // If the next screen node is provided, get its parent IDs
    //   const nextScreenParents = nextScreenNode ? this.getParentListId(nextScreenNode?.id) : [];

    //   // Get the parent IDs that are not shared with the next screen
    //   const parentIds = selectedParents.filter((parentId: any) => !nextScreenParents.includes(parentId));

    //   // Collapse all the parent nodes of the active screen that are not also parents of the next screen
    //   // this.menuTree.collapseAll(parentIds);
    // } else {
    //   // If no active screen is provided, collapse all the nodes
    //   // this.menuTree.collapseAll();
    // }
  }

  /**
   * Toggles the sidebar between collapsed and expanded states.
   * Emits the current state of the sidebar using the panelToggle event emitter.
   */
  public toggleSidebar() {
    this.collapsed = !this.collapsed;
    this.panelToggle.emit(this.collapsed);
  }

  /**
   * Handles the tree created event.
   * If the data source is not empty and the menu tree is available,
   * it will update the active screen by expanding all the parent nodes of the active screen.
   * @param event - The tree created event.
   */
  public onTreeCreated(event: any) {
    if (this.fields?.dataSource?.length && this.menuTree) {
      this.updateActiveScreen();
    }
  }
}
// /**
//  * Updates the selected menu item based on the current URL.
//  */
// private processActiveMenu() {
//   if (!this.activeScreen || !this.fields?.dataSource?.length) return;

//   // Find the menu item that matches Screen
//   const selectedNode = this.fields?.dataSource?.find((item: any) => item?.screenId === this.activeScreen);
//   if (selectedNode) {
//     // Set new selection
//     selectedNode.selected = true;
//     this.expandParents(selectedNode.parentID);
//   }
// }

// /**
//  * Expands the parent nodes of the selected node.
//  * @param parentID - The parent ID of the selected node.
//  */
// private expandParents(parentID: any) {
//   if (parentID) {
//     const parentNode = this.fields?.dataSource?.find((item: any) => item?.id === parentID);
//     if (parentNode) {
//       parentNode.expanded = true;
//       this.expandParents(parentNode?.parentID);
//     }
//   }
// }

// /**
//  * Clears the previous active menu.
//  */
// private clearPreviousActiveMenu() {
//   this.fields?.dataSource?.forEach((item: any) => {
//     item.isSelected = false;
//     item.expanded = false;
//   });
// }
